import { Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { MembershipInput } from 'sr-types/lib/person/v1/graphql';
import { BASIC_INFO_FORM_TYPE } from '../../../person/detailsPage/editComponents/BasicInfoFormUtils';
import type { EntitySecurity } from '../../auth/Security';
import { SECURITY_ACTIONS } from '../../auth/Security';
import type { SecuredActionType } from '../../components/ActionType';
import EllipsisText from '../../components/EllipsisText';
import ResponsiveActions from '../../components/ResponsiveActions';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { H, V } from '../../Layout';
import { useStyling } from '../../Theme';

export default function MembershipsSection({
    memberships,
    entitySecurity,
    editEnabled = undefined,
    onEdit = undefined
}: {
    memberships: Array<MembershipInput>;
    entitySecurity?: EntitySecurity;
    editEnabled?: boolean;
    onEdit?: (editFieldName: string) => void;
}) {
    const { theme, isMobile } = useStyling();

    const [showAddButton, setShowAddButton] = useState<boolean>(false);

    const membershipArray: Array<string> =
        memberships && memberships.map((m: MembershipInput) => m.organizationReference?.label);

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: ENTITY_CONSTANTS.person,
                entitySecurity,
                label: <I18n token="form.title.add" />,
                icon: Icons.AddButton,
                onClick: () => {
                    onEdit?.(BASIC_INFO_FORM_TYPE.membership);
                }
            }
        ],
        [entitySecurity, onEdit]
    );

    return (
        <V onMouseEnter={() => setShowAddButton(true)} onMouseLeave={() => setShowAddButton(false)}>
            <H sx={{ height: isMobile ? '45px' : '25px', justifyContent: isMobile && 'space-between' }}>
                <Typography color={theme.palette.text.secondary}>
                    <I18n token={'person.basic.info.form.memberships'} />
                </Typography>

                {editEnabled && (
                    <ResponsiveActions
                        isActivated={showAddButton}
                        securedActions={securedActions}
                        containerSx={{ p: 0 }}
                    />
                )}
            </H>
            {membershipArray && membershipArray.length ? (
                <EllipsisText text={membershipArray.join(', ')} />
            ) : (
                <H sx={{ color: theme.palette.text.disabled, width: 'fit-content' }}>
                    <EllipsisText text={<I18n token="no.data.added" />} />
                </H>
            )}
        </V>
    );
}
